/**
 * PlayerAnalyser — /league/:id/player-analyser (Analysers rebuild, Stage 3)
 *
 * Two-step flow:
 *   1. Roster list — user taps any player (each row now carries a team chip)
 *   2. Profile view — one JSON GET (no more streamed markdown the frontend
 *      used to regex-parse for `## Team Fit: XX%` / `## Next Steps: KEEP` —
 *      see the old TeamFitCard/PlayerContent this file used to carry).
 *      Three sections exactly as specified:
 *        1. Identity — photo, name, Position · Team · N% owned
 *        2. Stats — one StatTable, Season / Last 7 / Last 30 / Last Season,
 *           each row omitted when absent, fantasy points a real column
 *        3. JAI's thoughts — prose, verdict pill, conditional CTA, Save to Notes
 *      Then the shared StickyAskBar, replacing FollowUpChat.
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import AppShell from '../components/AppShell'
import ToolHeader from '../components/ToolHeader'
import PlayerPhoto from '../components/PlayerPhoto'
import JaiRichText from '../components/JaiRichText'
import StatTable from '../components/StatTable'
import StickyAskBar from '../components/StickyAskBar'
import UpgradePrompt from '../components/UpgradePrompt'
import TeamChip from '../components/TeamChip'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Card, StatusPill, PosChip, PrimaryBtn, solidChip, pctTone, RefreshButton, Notice } from '../components/JaiV2'
import { JAIMascot } from '../components/JAIMascot'
import { addNote } from '../services/notesStore'
import { NBA_STAT_COLUMNS, NFL_QB_STAT_COLUMNS, NFL_SKILL_STAT_COLUMNS } from '../config/statColumns'

// ── Verdict → color, the one place this mapping exists ─────────────────────
const VERDICT_META = {
  keep:  { label: 'KEEP',  color: V2.success },
  trade: { label: 'TRADE', color: V2.warning },
  drop:  { label: 'DROP',  color: V2.error },
  watch: { label: 'WATCH', color: V2.faint },
}

const CTA_META = {
  trade: { label: 'Shop in Trade Analyser →', to: (leagueId, name) => `/league/${leagueId}/trade/analyse?give=${encodeURIComponent(name)}` },
  waiver: { label: 'Find a replacement on Waiver Wire →', to: (leagueId, name) => `/league/${leagueId}/waiver?drop=${encodeURIComponent(name)}` },
}

// ── Stat table — Season / Last 7 Games / Last 30 Games / Last Season ───────
// Column sets live in config/statColumns.js (shared with TeamAnalyser.jsx's
// top performers, same underlying stat-window shape). StatTable caps at 5
// columns; fantasy points is always one of them (Stage 3 spec — "a column,
// not a footnote"). Everything not shown here (TO, 3PM, FG%/FT%, targets,
// etc.) is still in the raw JSON the ask bar can answer questions about.
const ROW_ORDER = [
  ['season', 'Season'],
  ['last7', 'Last 7 Games'],
  ['last30', 'Last 30 Games'],
  ['priorSeason', 'Last Season'],
]

function buildStatRows(stats, columns, sport) {
  if (!stats) return []
  return ROW_ORDER
    .filter(([key]) => stats[key])
    .map(([key, label]) => {
      const values = {}
      columns.forEach((c) => { values[c.key] = stats[key][c.key] })
      // NFL has no per-game log to derive a window size from otherwise — show
      // games played next to the label so "20 pts/gm in 4 games" isn't read
      // as "20 pts/gm in 16 games" (Job: NFL fantasy points goes per-game).
      const rowLabel = sport === 'nfl' && stats[key].gamesPlayed != null
        ? `${label} (${stats[key].gamesPlayed} gp)`
        : label
      return { label: rowLabel, values }
    })
}

function statLinesForAskBar(stats) {
  return ROW_ORDER
    .filter(([key]) => stats?.[key])
    .map(([key, label]) => `${label}: ${JSON.stringify(stats[key])}`)
    .join('\n')
}

// ── Roster list (Step 1) ────────────────────────────────────────────────

function RosterRow({ player, sport, onSelect }) {
  return (
    <button
      onClick={() => onSelect(player)}
      style={{
        display: 'flex', alignItems: 'center', gap: 13, textAlign: 'left',
        background: 'white', border: `1px solid ${V2.hairSoft}`, borderRadius: 12,
        padding: '11px 16px', cursor: 'pointer', boxShadow: '0 1px 4px rgba(10,31,68,0.03)',
        transition: 'border-color 180ms', fontFamily: JAI_FONTS.tight, width: '100%',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(30,91,214,0.35)' }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = V2.hairSoft }}
    >
      <PlayerPhoto playerId={player.playerId} name={player.name} size={40} rounded="sm" />
      <span style={{ fontSize: 14, fontWeight: 700, color: V2.navy }}>{player.name}</span>
      <PosChip>{player.position}</PosChip>
      <TeamChip abbr={player.team} sport={sport} />
      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
        {player.injuryStatus && player.injuryStatus !== 'ACTIVE' && <StatusPill status={player.injuryStatus} />}
        <span style={{ color: 'rgba(10,31,68,0.25)', fontSize: 14 }}>→</span>
      </span>
    </button>
  )
}

// ── Main component ─────────────────────────────────────────────────────────

export default function PlayerAnalyser() {
  const { id } = useParams()

  const [league, setLeague] = useState(null)
  const [roster, setRoster] = useState([])
  const [rosterLoading, setRosterLoading] = useState(true)

  const [selectedName, setSelectedName] = useState(null)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [upgrade, setUpgrade] = useState(null)
  const [noteSaved, setNoteSaved] = useState(false)
  const [noteSaving, setNoteSaving] = useState(false)
  const [noteError, setNoteError] = useState('')

  const sport = (league?.sport || 'nba').toLowerCase()

  useEffect(() => {
    const init = async () => {
      try {
        const [leagueRes, rosterRes] = await Promise.allSettled([
          leaguesApi.get(id),
          leaguesApi.getRoster(id),
        ])
        if (leagueRes.status === 'fulfilled') setLeague(leagueRes.value.data.league)
        if (rosterRes.status === 'fulfilled') setRoster(rosterRes.value.data.players || [])
      } catch { /* non-fatal */ }
      setRosterLoading(false)
    }
    init()
  }, [id])

  const runAnalysis = useCallback(async (playerName, { refresh = false } = {}) => {
    if (refresh) setRefreshing(true)
    else setLoading(true)
    setError('')
    setUpgrade(null)
    try {
      const res = await leaguesApi.analysePlayer(id, playerName, { refresh })
      setData(res.data)
      setNoteSaved(false)
    } catch (err) {
      if (err.response?.data?.code === 'PLAN_UPGRADE_REQUIRED') {
        setUpgrade({ tool: err.response.data.requiredTool, message: err.response.data.error })
      } else {
        setError(err.response?.data?.error || 'Something went wrong. Please try again.')
      }
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [id])

  const handleSelectPlayer = (player) => {
    setSelectedName(player.name)
    setData(null)
    setError('')
    runAnalysis(player.name)
  }

  const saveToNotes = async () => {
    if (noteSaved || noteSaving || !data?.thoughts) return
    setNoteSaving(true)
    setNoteError('')
    try {
      await addNote(id, data.thoughts, { source: 'player-analyser', title: `Player Analyser — ${data.player?.name || selectedName}` })
      setNoteSaved(true)
    } catch {
      setNoteError('Could not save this to your notes. Try again.')
    } finally {
      setNoteSaving(false)
    }
  }

  if (rosterLoading) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center h-full min-h-screen gap-3">
          <JAIMascot size={56} state="thinking" animate={true} />
          <p className="text-sm" style={{ color: V2.faint }}>Loading roster…</p>
        </div>
      </AppShell>
    )
  }

  const columns = sport === 'nfl'
    ? (data?.player?.position === 'QB' ? NFL_QB_STAT_COLUMNS : NFL_SKILL_STAT_COLUMNS)
    : NBA_STAT_COLUMNS
  const statRows = buildStatRows(data?.stats, columns, sport)
  const verdictMeta = data?.verdict ? (VERDICT_META[data.verdict] || VERDICT_META.watch) : null
  const ctaMeta = data?.cta ? CTA_META[data.cta.kind] : null

  const askBarContext = data
    ? `THE USER IS LOOKING AT ${data.player.name}'S PROFILE RIGHT NOW.\n` +
      `${data.player.position || '?'} · ${data.player.team || 'FA'} · ${data.player.ownershipPercent != null ? `${data.player.ownershipPercent}% owned` : 'ownership unknown'}\n` +
      `${statLinesForAskBar(data.stats)}\nJAI's verdict: ${data.verdict}\nJAI's thoughts: ${data.thoughts}`
    : undefined

  return (
    <AppShell>
      <div style={{ padding: '30px 40px', fontFamily: JAI_FONTS.tight }}>
        <div style={{ maxWidth: 880, margin: '0 auto', paddingBottom: 100 }}>
          <ToolHeader
            leagueName={league?.name}
            title="Player Analyser"
            subtitle={selectedName ? undefined : 'Pick a player from your roster — JAI runs the full breakdown.'}
            back={{ label: 'Go to League Home Screen', to: `/league/${id}` }}
          />

          {/* ── Step 1: Roster selector ───────────────────────────────── */}
          {!selectedName && (
            roster.length === 0 ? (
              <V2Card pad="28px 24px" style={{ textAlign: 'center' }}>
                <p style={{ fontSize: 13.5, color: V2.mute, margin: '0 0 6px' }}>No roster data found.</p>
                <p style={{ fontSize: 12, color: V2.faint, margin: 0 }}>Make sure your league is synced, then come back.</p>
              </V2Card>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {roster.map((player, i) => (
                  <RosterRow key={player.id || player.playerId || i} player={player} sport={sport} onSelect={handleSelectPlayer} />
                ))}
              </div>
            )
          )}

          {/* ── Step 2: Profile view ──────────────────────────────────── */}
          {selectedName && (
            <>
              {upgrade && <UpgradePrompt tool={upgrade.tool} message={upgrade.message} />}

              {error && !upgrade && (
                <Notice tone="error" action="Try again" onAction={() => runAnalysis(selectedName)}>{error}</Notice>
              )}

              {/* Inline spinner while loading — never a full-page loader */}
              {loading && !error && !upgrade && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 0', gap: 12 }}>
                  <JAIMascot size={56} state="thinking" animate={true} />
                  <p style={{ fontSize: 13.5, color: V2.faint, margin: 0 }}>Analysing {selectedName}…</p>
                </div>
              )}

              {!loading && !error && !upgrade && data && (
                <>
                  {/* Section 1 — identity. Nothing else in this block. */}
                  <V2Card pad="20px 22px" style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <PlayerPhoto playerId={data.player.playerId} name={data.player.name} size={58} rounded="full" />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 22, fontWeight: 800, color: V2.navy, letterSpacing: '-0.02em' }}>{data.player.name}</span>
                          <StatusPill status={data.player.injuryStatus || 'ACTIVE'} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: V2.faint, marginTop: 4 }}>
                          {[data.player.position, data.player.team ? String(data.player.team).toUpperCase() : null].filter(Boolean).join(' · ')}
                          {data.player.ownershipPercent != null && (
                            <>
                              {(data.player.position || data.player.team) && <span>·</span>}
                              <span style={{ color: pctTone(data.player.ownershipPercent), fontWeight: 700 }}>{Math.round(data.player.ownershipPercent)}% owned</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div style={{ marginLeft: 'auto', display: 'flex', flexShrink: 0 }}>
                        <RefreshButton onClick={() => runAnalysis(selectedName, { refresh: true })} loading={refreshing} credits="playerAnalyser" style={{ fontSize: 11.5, padding: '5px 12px' }} />
                      </div>
                    </div>
                  </V2Card>

                  {/* Section 2 — stats. */}
                  {statRows.length > 0 && <StatTable title="Stats" columns={columns} rows={statRows} />}

                  {/* Section 3 — JAI's thoughts. */}
                  <V2Card pad="18px 22px" style={{ marginTop: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                      <JAIMascot size={30} state="idle" animate={false} />
                      <span style={{ fontSize: 13.5, fontWeight: 800, color: V2.navy, letterSpacing: '0.06em', textTransform: 'uppercase' }}>JAI's thoughts</span>
                      {verdictMeta && (
                        <span style={{
                          fontSize: 11, fontWeight: 800, padding: '3px 12px', borderRadius: 999, letterSpacing: '0.06em',
                          ...solidChip(verdictMeta.color), marginLeft: 4,
                        }}>
                          {verdictMeta.label}
                        </span>
                      )}
                      {data.teamFit != null && (
                        <span style={{ fontSize: 11.5, color: pctTone(data.teamFit), fontWeight: 700, fontFamily: JAI_FONTS.mono, marginLeft: 'auto' }}>
                          {data.teamFit}% team fit
                        </span>
                      )}
                    </div>

                    <JaiRichText content={data.thoughts} />

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
                      {ctaMeta && (
                        <Link to={ctaMeta.to(id, data.player.name)} style={{ textDecoration: 'none' }}>
                          <PrimaryBtn>{ctaMeta.label}</PrimaryBtn>
                        </Link>
                      )}
                      <button
                        onClick={saveToNotes}
                        disabled={noteSaved || noteSaving}
                        style={{
                          background: 'none', border: 'none', color: noteSaved ? V2.faint : V2.blue,
                          fontWeight: 700, cursor: (noteSaved || noteSaving) ? 'default' : 'pointer', fontSize: 12.5, padding: 0,
                          fontFamily: JAI_FONTS.tight,
                        }}
                      >
                        {noteSaved ? 'Saved to My Notes ✓' : noteSaving ? 'Saving…' : 'Save to My Notes →'}
                      </button>
                      {noteError && <span style={{ fontSize: 12, color: V2.error }}>{noteError}</span>}
                    </div>
                  </V2Card>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <StickyAskBar
        leagueId={id}
        scope="player"
        context={askBarContext}
        placeholder={selectedName ? `Ask JAI about ${selectedName}` : 'Ask JAI a follow-up'}
      />
    </AppShell>
  )
}
